import { DailySummary, DailyExpense, CreateExpenseData, CloseCashRegisterData, OpenCashRegisterData, CashCount, ShiftStatus, ShiftSummary, AllShiftsData } from '../types/cash-register.types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

// Helper function to get auth token
const getAuthToken = () => {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
  return token;
};

// Helper function to make API requests
const apiRequest = async (url: string, options: RequestInit = {}) => {
  const token = getAuthToken();
  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Network error' }));
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
};

export const cashRegisterService = {
  async isCashRegisterOpened(locationId: string, date: string): Promise<boolean> {
    const data = await apiRequest(`/cash-register/${locationId}/${date}/status`);
    return data.isOpened;
  },

  async getCashRegisterStatus(locationId: string, date: string): Promise<ShiftStatus> {
    const data = await apiRequest(`/cash-register/${locationId}/${date}/status`);
    return {
      isOpened: data.isOpened,
      currentShift: data.currentShift,
      actualShift: data.actualShift,
      shiftSchedule: data.shiftSchedule
    };
  },

  async openCashRegister(openData: OpenCashRegisterData): Promise<{ success: boolean; message: string }> {
    const data = await apiRequest(`/cash-register/${openData.locationId}/open`, {
      method: 'POST',
      body: JSON.stringify({
        date: openData.date,
        openingAmount: openData.openingAmount,
      }),
    });
    
    return {
      success: data.success,
      message: data.message,
    };
  },

  async getDailySummary(locationId: string, date: string): Promise<DailySummary> {
    const data = await apiRequest(`/cash-register/${locationId}/${date}/summary`);
    return data;
  },

  async getShiftSummary(locationId: string, date: string, shift: string): Promise<DailySummary> {
    const data = await apiRequest(`/cash-register/${locationId}/${date}/${shift.toLowerCase()}/summary`);
    return data;
  },

  async getCurrentSessionSummary(locationId: string, date: string): Promise<DailySummary | null> {
    try {
      console.log('🔍 Getting current session summary for:', locationId, date);
      // Get all shifts to find the currently open one
      const allShifts = await this.getAllShifts(locationId, date);
      const openSession = allShifts.shifts.find(session => session.isOpen);

      if (!openSession) {
        console.log('❌ No open session found');
        return null;
      }

      // Read totals directly from CashRegister fields (already come from all-shifts endpoint)
      // Get expenses specifically for this session
      let sessionExpenses = [];
      try {
        const dailySummary = await this.getDailySummary(locationId, date);
        sessionExpenses = (dailySummary.expenses || []).filter((expense: any) =>
          expense.cashRegisterId === openSession.id
        );
      } catch (expenseError) {
        console.warn('Could not load session-specific expenses:', expenseError);
      }

      const result = {
        totalSales: openSession.totalSales || 0,
        salesCount: openSession.salesCount || 0,
        cashSales: openSession.cashSales || 0,
        cardSales: openSession.cardSales || 0,
        yapePlinSales: openSession.yapePlinSales || 0,
        appSales: openSession.appSales || 0,
        openingAmount: parseFloat(openSession.openingAmount) || 0,
        cancelledSalesAmount: openSession.cancelledSalesAmount || 0,
        cancelledVouchersCount: openSession.cancelledVouchersCount || 0,
        expenses: sessionExpenses,
        date,
        shift: openSession.shift,
        currentShift: openSession.shift,
        locationId,
        cashRegistersCount: 1
      };

      console.log('✅ Current session summary result:', result);
      return result;
    } catch (error) {
      console.error('Error getting current session summary:', error);
      return null;
    }
  },

  async getAllShifts(locationId: string, date: string): Promise<AllShiftsData> {
    const data = await apiRequest(`/cash-register/${locationId}/${date}/all-shifts`);
    return data;
  },

  async createExpense(expenseData: CreateExpenseData): Promise<DailyExpense> {
    const data = await apiRequest(`/cash-register/${expenseData.locationId}/expenses`, {
      method: 'POST',
      body: JSON.stringify({
        description: expenseData.description,
        amount: expenseData.amount,
        category: expenseData.category,
        type: expenseData.type,
      }),
    });
    
    return data;
  },

  async closeCashRegister(closeData: CloseCashRegisterData): Promise<{ success: boolean; message: string }> {
    const data = await apiRequest(`/cash-register/${closeData.locationId}/close`, {
      method: 'POST',
      body: JSON.stringify({
        date: closeData.date,
        cashCount: closeData.cashCount,
        finalBalance: closeData.finalBalance,
      }),
    });
    
    return {
      success: data.success,
      message: data.message,
    };
  },

  async deleteExpense(expenseId: string): Promise<{ success: boolean; message: string }> {
    const data = await apiRequest(`/cash-register/expenses/${expenseId}`, {
      method: 'DELETE',
    });
    
    return {
      success: data.success,
      message: data.message,
    };
  },

  async getCashRegisterHistory(locationId: string): Promise<any[]> {
    const data = await apiRequest(`/cash-register/${locationId}/history`);
    return data;
  },

  async getCashCount(locationId: string, date: string): Promise<CashCount | null> {
    try {
      const response = await apiRequest(`/cash-count/${locationId}/${date}`);
      if (response.success && response.data) {
        return {
          bills: response.data.bills || {},
          coins: response.data.coins || {},
          totalCounted: response.data.totalCounted || 0,
          isSaved: true
        };
      }
      return null;
    } catch (error: any) {
      if (error.message.includes('404') || error.message.includes('No se encontró conteo de efectivo')) {
        return null;
      }
      throw error;
    }
  },

  async saveCashCount(locationId: string, date: string, cashCount: CashCount, countedBy?: string): Promise<CashCount> {
    const data = await apiRequest(`/cash-count/${locationId}/${date}`, {
      method: 'POST',
      body: JSON.stringify({
        bills: cashCount.bills,
        coins: cashCount.coins,
        countedBy,
      }),
    });
    
    return {
      bills: data.data.bills,
      coins: data.data.coins,
      totalCounted: data.data.totalCounted,
      isSaved: true
    };
  },

  async updateCashCount(locationId: string, date: string, cashCount: CashCount, countedBy?: string): Promise<CashCount> {
    const data = await apiRequest(`/cash-count/${locationId}/${date}`, {
      method: 'PUT',
      body: JSON.stringify({
        bills: cashCount.bills,
        coins: cashCount.coins,
        countedBy,
      }),
    });
    
    return {
      bills: data.data.bills,
      coins: data.data.coins,
      totalCounted: data.data.totalCounted,
      isSaved: true
    };
  },

  async deleteCashCount(locationId: string, date: string): Promise<{ success: boolean; message: string }> {
    const data = await apiRequest(`/cash-count/${locationId}/${date}`, {
      method: 'DELETE',
    });
    
    return {
      success: data.success,
      message: data.message,
    };
  },

  async getSuggestedOpeningAmount(locationId: string): Promise<number> {
    try {
      const data = await apiRequest(`/cash-register/${locationId}/suggested-opening-amount`);
      return data.suggestedAmount || 0;
    } catch (error) {
      console.warn('Could not get suggested opening amount:', error);
      return 0;
    }
  }
};